import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { 
  FinanceCustomer,
  Project,
  Item,
  TaxRate,
  PaymentTerm,
  Currency,
  InvoiceCategory,
  ApiResponse 
} from '@/api/types'
import { filterTaxesByKind, TAX_SALES } from '@/lib/documentTax'

/**
 * Finance service for shared finance APIs
 * Used by quotations, invoices, etc.
 */
export class FinanceService {
  /**
   * Get all customers for a branch
   */
  async getAllCustomers(branchId: number): Promise<FinanceCustomer[]> {
    const url = endpoints.finance.customers(branchId)
    const response = await apiClient.get<ApiResponse<FinanceCustomer[]> | FinanceCustomer[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get projects for a customer and branch
   */
  async getProjects(branchId: number, customerId: number): Promise<Project[]> {
    const url = endpoints.finance.projects(branchId, customerId)
    const response = await apiClient.get<ApiResponse<Project[]> | Project[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get item list for a branch
   */
  async getItemList(branchId: number): Promise<Item[]> {
    const url = endpoints.finance.items(branchId)
    const response = await apiClient.get<ApiResponse<Item[]> | Item[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get tax rates for a branch
   */
  async getTaxRates(branchId: number): Promise<TaxRate[]> {
    const url = endpoints.finance.taxRates(branchId)
    const response = await apiClient.get<ApiResponse<TaxRate[]> | TaxRate[]>(url)
    const list = Array.isArray(response.data)
      ? response.data
      : response.data && 'data' in response.data && Array.isArray(response.data.data)
        ? response.data.data
        : []
    return filterTaxesByKind(list, TAX_SALES)
  }

  /**
   * Get payment terms
   */
  async getPaymentTerms(): Promise<PaymentTerm[]> {
    const url = endpoints.finance.paymentTerms
    const response = await apiClient.get<ApiResponse<PaymentTerm[]> | PaymentTerm[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get currencies
   */
  async getCurrencies(): Promise<Currency[]> {
    const url = endpoints.finance.currencies
    const response = await apiClient.get<ApiResponse<Currency[]> | Currency[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get invoice categories
   */
  async getInvoiceCategories(): Promise<InvoiceCategory[]> {
    const url = endpoints.finance.invoiceCategories
    const response = await apiClient.get<ApiResponse<InvoiceCategory[]> | InvoiceCategory[]>(url)
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get all accounts for General Entry (from ApiBills)
   */
  async getAllAccountsForVoucher(branchId: number): Promise<any[]> {
    try {
      const url = endpoints.finance.getAllAccounts(branchId)
      console.log('🔵 [getAllAccountsForVoucher] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
      console.log('🔵 [getAllAccountsForVoucher] Response:', response)
      
      if (Array.isArray(response.data)) {
        console.log('🔵 [getAllAccountsForVoucher] Returning array:', response.data.length, 'items')
        return response.data
      }
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          console.log('🔵 [getAllAccountsForVoucher] Returning from data property:', data.length, 'items')
          return data
        }
      }
      console.warn('⚠️ [getAllAccountsForVoucher] Unexpected response format:', response.data)
      return []
    } catch (error: any) {
      console.error('❌ [getAllAccountsForVoucher] Error:', error)
      throw error
    }
  }

  /**
   * Get projects for General Entry (from ApiBills)
   */
  async getProjectsForVoucher(branchId: number): Promise<Project[]> {
    try {
      const url = endpoints.finance.getProject(branchId)
      console.log('🔵 [getProjectsForVoucher] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<Project[]> | Project[]>(url)
      console.log('🔵 [getProjectsForVoucher] Response:', response)
      
      if (Array.isArray(response.data)) {
        console.log('🔵 [getProjectsForVoucher] Returning array:', response.data.length, 'items')
        return response.data
      }
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          console.log('🔵 [getProjectsForVoucher] Returning from data property:', data.length, 'items')
          return data
        }
      }
      console.warn('⚠️ [getProjectsForVoucher] Unexpected response format:', response.data)
      return []
    } catch (error: any) {
      console.error('❌ [getProjectsForVoucher] Error:', error)
      throw error
    }
  }

  /**
   * Get profit centers (from ApiBills)
   */
  async getProfitCenters(branchId?: number): Promise<any[]> {
    try {
      const url = endpoints.finance.getProfitCenter(branchId)
      console.log('🔵 [getProfitCenters] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
      console.log('🔵 [getProfitCenters] Response:', response)
      
      if (Array.isArray(response.data)) {
        console.log('🔵 [getProfitCenters] Returning array:', response.data.length, 'items')
        return response.data
      }
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          console.log('🔵 [getProfitCenters] Returning from data property:', data.length, 'items')
          return data
        }
      }
      console.warn('⚠️ [getProfitCenters] Unexpected response format:', response.data)
      return []
    } catch (error: any) {
      console.error('❌ [getProfitCenters] Error:', error)
      throw error
    }
  }

  /**
   * Get cost locations (from ApiBills)
   */
  async getCostLocations(branchId?: number): Promise<any[]> {
    try {
      const url = endpoints.finance.getCostLocation(branchId)
      console.log('🔵 [getCostLocations] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
      console.log('🔵 [getCostLocations] Response:', response)
      
      if (Array.isArray(response.data)) {
        console.log('🔵 [getCostLocations] Returning array:', response.data.length, 'items')
        return response.data
      }
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          console.log('🔵 [getCostLocations] Returning from data property:', data.length, 'items')
          return data
        }
      }
      console.warn('⚠️ [getCostLocations] Unexpected response format:', response.data)
      return []
    } catch (error: any) {
      console.error('❌ [getCostLocations] Error:', error)
      throw error
    }
  }

  /**
   * Get all vendors for General Entry (from ApiBills)
   */
  async getAllVendorsForVoucher(branchId: number): Promise<any[]> {
    try {
      const url = endpoints.finance.getAllVendors(branchId)
      console.log('🔵 [getAllVendorsForVoucher] Calling:', url, 'branchId:', branchId)
      const response = await apiClient.get<ApiResponse<any[]> | any[]>(url)
      console.log('🔵 [getAllVendorsForVoucher] Response:', response)
      
      if (Array.isArray(response.data)) {
        console.log('🔵 [getAllVendorsForVoucher] Returning array:', response.data.length, 'items')
        return response.data
      }
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          console.log('🔵 [getAllVendorsForVoucher] Returning from data property:', data.length, 'items')
          return data
        }
      }
      console.warn('⚠️ [getAllVendorsForVoucher] Unexpected response format:', response.data)
      return []
    } catch (error: any) {
      console.error('❌ [getAllVendorsForVoucher] Error:', error)
      throw error
    }
  }
}

export const financeService = new FinanceService()

